import * as React from "react"
import { cn } from "@/lib/utils"
import { useCompactMode } from "@/hooks/useCompactMode"

const GlassCard = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    variant?: 'subtle' | 'default' | 'strong';
    hover?: boolean;
  }
>(({ className, variant = 'default', hover = true, ...props }, ref) => {
  const variants = {
    subtle: 'bg-white/40 dark:bg-slate-900/40 backdrop-blur-md border border-white/10 dark:border-white/5',
    default: 'bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl border border-white/20 dark:border-white/10',
    strong: 'bg-white/90 dark:bg-slate-900/90 backdrop-blur-2xl border border-white/30 dark:border-white/15',
  };

  return (
    <div
      ref={ref}
      className={cn(
        "rounded-2xl shadow-lg transition-all duration-300",
        variants[variant],
        hover && "hover:shadow-xl",
        className
      )}
      {...props}
    />
  )
})
GlassCard.displayName = "GlassCard"

// Header / Content / Footer paddings shrink in compact mode so dense
// pages fit more content above the fold without the caller having to
// override every className.
const GlassCardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { cx } = useCompactMode()
  return (
    <div
      ref={ref}
      className={cn("flex flex-col space-y-1.5", cx("p-6", "p-4"), className)}
      {...props}
    />
  )
})
GlassCardHeader.displayName = "GlassCardHeader"

type GlassCardTitleProps = React.HTMLAttributes<HTMLHeadingElement> & {
  /**
   * Heading level to render. Defaults to `h3` for backward compatibility, but
   * pages can opt into the correct level (e.g. `h2` under a page-level `h1`)
   * to satisfy axe's `heading-order` rule.
   */
  as?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
};

const GlassCardTitle = React.forwardRef<
  HTMLHeadingElement,
  GlassCardTitleProps
>(({ className, as: Tag = "h3", ...props }, ref) => {
  const { cx } = useCompactMode()
  return (
    <Tag
      ref={ref}
      className={cn(
        cx("text-2xl", "text-xl"),
        "font-semibold leading-none tracking-tight",
        className
      )}
      {...props}
    />
  )
})
GlassCardTitle.displayName = "GlassCardTitle"

const GlassCardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
GlassCardDescription.displayName = "GlassCardDescription"

const GlassCardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { cx } = useCompactMode()
  return (
    <div ref={ref} className={cn(cx("p-6", "p-4"), "pt-0", className)} {...props} />
  )
})
GlassCardContent.displayName = "GlassCardContent"

const GlassCardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { cx } = useCompactMode()
  return (
    <div
      ref={ref}
      className={cn("flex items-center", cx("p-6", "p-4"), "pt-0", className)}
      {...props}
    />
  )
})
GlassCardFooter.displayName = "GlassCardFooter"

export { GlassCard, GlassCardHeader, GlassCardFooter, GlassCardTitle, GlassCardDescription, GlassCardContent }
